import { TpWebGL2DRenderData } from '../../../components/webgl-2d/interface-renderData';
import { TpWebGl2DContext, TpWebGl2DScale } from '../../../components/webgl-2d/interfaces';
import { renderWebGL2D } from '../../../components/webgl-2d/renderWebGL2D';
import { color } from '../helpers/colors';
import { TpChartColorSchema } from './helpers';

export interface TpLogicalRange {
  rmin: number;
  rmax: number;
}

export function createLogicalRange(rmin: number, rmax: number): TpLogicalRange {
  return { rmin, rmax };
}

export enum POSITION_DEFINITION {
  AbsoluteMinDiff = 'AbsoluteMinDiff',
  AbsoluteMaxDiff = 'AbsoluteMaxDiff',
}

export interface TpRenderPosition {
  posit: number;
  type: POSITION_DEFINITION;
}

export interface TpRenderRange {
  rmin: TpRenderPosition;
  rmax: TpRenderPosition;
}

// const defaultLogicalRange: TpLogicalRange = { rmin: 0, rmax: 1 };

export function RenderMinDiffAbs(posit: number): TpRenderPosition {
  return { type: POSITION_DEFINITION.AbsoluteMinDiff, posit };
}

export function RenderMaxDiffAbs(posit: number): TpRenderPosition {
  return { type: POSITION_DEFINITION.AbsoluteMaxDiff, posit };
}

export function createRenderRange(rmin: TpRenderPosition, rmax: TpRenderPosition): TpRenderRange {
  return { rmin, rmax };
}

export interface TpChartAreaDefinition {
  id: string;
  logicalRangeX?: TpLogicalRange;
  logicalRangeY?: TpLogicalRange;
  renderRangeX: TpRenderRange;
  renderRangeY: TpRenderRange;
  syncXWith?: TpChartAreaDefinition; // if set, the X axis is synced with the referred area (useful for e.g. enforcing of fixed aspect ratio)
  syncYWith?: TpChartAreaDefinition; // if set, the Y axis is synced with the referred area (useful for e.g. enforcing of fixed aspect ratio)
  zoomPanX?: boolean;
  zoomPanY?: boolean;
  forceSquareApectRatio?: boolean;
  canAreaSelect?: string; //"x" | "y" | "xy",
  render: (renderContext: TpAreaRenderContext) => void;
  renderOverlay?: (renderContext: TpAreaRenderContext) => void;
  webGL2DRenderData?: TpWebGL2DRenderData;
}

export interface TpZoomPanInfo {
  zoomFactor: number;
  pan: number; // defined as a fraction of the logical range
}

export interface TpZoomPaninfoDual {
  x: TpZoomPanInfo;
  y: TpZoomPanInfo;
}

export function getRenderPos(
  renderBoxMin: number,
  renderBoxMax: number,
  pixelRatio: number,
  renderPos: TpRenderPosition,
): number {
  if (renderPos.type == POSITION_DEFINITION.AbsoluteMinDiff) return renderBoxMin + renderPos.posit * pixelRatio;
  if (renderPos.type == POSITION_DEFINITION.AbsoluteMaxDiff) return renderBoxMax - renderPos.posit * pixelRatio;
  throw 'Invalid position definition'; // should never be reached
}

export function createAreaPositioningCss(area: TpChartAreaDefinition) {
  // NOTE this logic is not sufficiently generic --  @todo: make more generic
  return {
    left: `${area.renderRangeX.rmin.posit}px`,
    top: `${area.renderRangeY.rmin.posit}px`,
    width: `calc(100% - ${area.renderRangeX.rmin.posit + area.renderRangeX.rmax.posit}px)`,
    height: `calc(100% - ${area.renderRangeY.rmin.posit + area.renderRangeY.rmax.posit}px)`,
  };
}

export interface TpZoomPanelCoordMapper {
  swap: boolean;
  logicalRange: TpLogicalRange;
  map: (logicalValue: number) => number;
  mapInv: (renderValue: number) => number;
  renderMin: number;
  renderMax: number;
  panConversionFactor: number;
}

export interface TpZoomPanelCoorDualMapper {
  mapX: TpZoomPanelCoordMapper;
  mapY: TpZoomPanelCoordMapper;
}

export function generateMapper(
  renderBoxMin: number,
  renderBoxMax: number,
  renderRange: TpRenderRange,
  logicalRange: TpLogicalRange | null | undefined,
  pixelRatio: number,
  zoomPanInfo: TpZoomPanInfo | null,
  swap: boolean,
): TpZoomPanelCoordMapper {
  let logicalRangeLoc = logicalRange;
  let renderMin = getRenderPos(renderBoxMin, renderBoxMax, pixelRatio, renderRange.rmin);
  let renderMax = getRenderPos(renderBoxMin, renderBoxMax, pixelRatio, renderRange.rmax);
  if (!logicalRangeLoc) {
    logicalRangeLoc = { rmin: renderMin, rmax: renderMax };
  }

  if (swap) {
    const tmp = renderMax;
    renderMax = renderMin;
    renderMin = tmp;
  }

  const ZPfactor = zoomPanInfo ? zoomPanInfo.zoomFactor : 1;
  const ZPoffset = zoomPanInfo ? zoomPanInfo.pan : 0;

  const renderRangeSize = renderMax - renderMin;
  const logicalRangeSize = logicalRangeLoc.rmax - logicalRangeLoc.rmin;

  // conversion: l= logical coord
  // step 1: lf = (l-lmin) / (lmax-lmin)      | convert to fraction of logical range
  // step 2: lfzp = (lf+pan) * zoom           | zoom & pan lf (pan = fraction of total logical range)
  // step 3: r = rmin + lfzp * (rmax-rmin)    | convert zoomed & panned fraction to render coords

  const factor = (ZPfactor * renderRangeSize) / logicalRangeSize;
  const offset = (-logicalRangeLoc.rmin / logicalRangeSize + ZPoffset) * ZPfactor * renderRangeSize + renderMin;

  return {
    swap,
    logicalRange: logicalRangeLoc,
    map: (logicalValue: number): number => logicalValue * factor + offset,
    mapInv: (renderValue: number): number => (renderValue - offset) / factor,
    renderMin,
    renderMax,
    panConversionFactor: 1 / (renderRangeSize * ZPfactor),
  };
}

export function generateDualMappers(
  renderBoxMinX: number,
  renderBoxMaxX: number,
  renderBoxMinY: number,
  renderBoxMaxY: number,
  area: TpChartAreaDefinition,
  zoomPanInfo: TpZoomPaninfoDual,
  pixelRatio: number,
): TpZoomPanelCoorDualMapper {
  let logicalRangeX = area.logicalRangeX;
  let logicalRangeY = area.logicalRangeY;

  if (area.forceSquareApectRatio && area.logicalRangeX && area.logicalRangeY) {
    const renderMinX = getRenderPos(renderBoxMinX, renderBoxMaxX, pixelRatio, area.renderRangeX.rmin);
    const renderMaxX = getRenderPos(renderBoxMinX, renderBoxMaxX, pixelRatio, area.renderRangeX.rmax);
    const renderMinY = getRenderPos(renderBoxMinY, renderBoxMaxY, pixelRatio, area.renderRangeY.rmin);
    const renderMaxY = getRenderPos(renderBoxMinY, renderBoxMaxY, pixelRatio, area.renderRangeY.rmax);
    logicalRangeX = { ...area.logicalRangeX! };
    logicalRangeY = { ...area.logicalRangeY! };
    const imageAspectRatio = (renderMaxY - renderMinY) / (renderMaxX - renderMinX);
    const logicalRangeValueX = area.logicalRangeX!.rmax - area.logicalRangeX!.rmin;
    const logicalRangeValueY = area.logicalRangeY!.rmax - area.logicalRangeY!.rmin;
    const logicalAspectRatio = logicalRangeValueY / logicalRangeValueX;
    if (imageAspectRatio > logicalAspectRatio) {
      // we need to compress the Y axis,  by expanding the logical Y range
      const rangeExpansion = (logicalRangeValueY / logicalAspectRatio) * imageAspectRatio - logicalRangeValueY;
      logicalRangeY!.rmin -= rangeExpansion / 2;
      logicalRangeY!.rmax += rangeExpansion / 2;
    } else {
      // we need to compress the X axis, by expanding the logical X rabge
      const rangeExpansion = (logicalRangeValueX / imageAspectRatio) * logicalAspectRatio - logicalRangeValueX;
      logicalRangeX!.rmin -= rangeExpansion / 2;
      logicalRangeX!.rmax += rangeExpansion / 2;
    }
  }
  return {
    mapX: generateMapper(
      renderBoxMinX,
      renderBoxMaxX,
      area.renderRangeX,
      logicalRangeX,
      pixelRatio,
      area.zoomPanX ? zoomPanInfo.x : null,
      false,
    ),
    mapY: generateMapper(
      renderBoxMinY,
      renderBoxMaxY,
      area.renderRangeY,
      logicalRangeY,
      pixelRatio,
      area.zoomPanY ? zoomPanInfo.y : null,
      true,
    ),
  };
}

export function isInRenderRange(mapper: TpZoomPanelCoordMapper, screenCoord: number | null) {
  if (screenCoord == null) return true;
  if (!mapper.swap) return screenCoord >= mapper.renderMin && screenCoord <= mapper.renderMax;
  else return screenCoord >= mapper.renderMax && screenCoord <= mapper.renderMin;
}

export interface TpZoomPanelInvPointMapInfo {
  area: TpChartAreaDefinition;
  mapX: TpZoomPanelCoordMapper;
  mapY: TpZoomPanelCoordMapper;
  mapsize: (size: number) => number;
  logicX: number;
  logicY: number;
}

export function invMapRenderCoord(
  canvasElem: any,
  pixelRatio: number,
  zoomPanInfo: TpZoomPaninfoDual,
  areas: TpChartAreaDefinition[],
  renderX: number,
  renderY: number,
): TpZoomPanelInvPointMapInfo | null {
  const width = canvasElem.width;
  const height = canvasElem.height;
  const mapsize = (size: number): number => size * pixelRatio;
  for (const area of areas) {
    const { mapX, mapY } = generateDualMappers(0, width, 0, height, area, zoomPanInfo, pixelRatio);
    if (
      renderX >= mapX.renderMin &&
      renderX <= mapX.renderMax &&
      renderY >= mapY.renderMax &&
      renderY <= mapY.renderMin
    )
      return {
        area,
        mapX,
        mapY,
        mapsize,
        logicX: mapX.mapInv(renderX),
        logicY: mapY.mapInv(renderY),
      };
  }
  return null;
}

function setCanvasFont(canvasContext: any, pixelRatio: number, size: number, bold?: boolean) {
  let fontStr = `${size * pixelRatio}pt Source Sans Pro`;
  if (bold) fontStr = 'bold ' + fontStr;
  canvasContext.font = fontStr;
}

export interface TpAreaRenderContext {
  mapx: (posit: number) => number;
  mapy: (posit: number) => number;
  mapsize: (size: number) => number;
  mapinvx: (posit: number) => number;
  mapinvy: (posit: number) => number;
  logicalRangeX: TpLogicalRange;
  logicalRangeY: TpLogicalRange;
  mapsizeinv: (size: number) => number;
  setFont: (size: number, bold?: boolean) => void;
  pixelConv: (size: number) => number;
  pixelInvConv: (size: number) => number;
  canvasElem: any;
  canvasContext: any;
  pixelRatio: number;
  areaXMin: number;
  areaXMax: number;
  areaYMin: number;
  areaYMax: number;
  axisDefX?: any;
  axisDefY?: any;
  colorSchema: TpChartColorSchema;
}

export function renderChartAreas(
  canvasElem: any,
  canvasContext: any,
  pixelRatio: number,
  zoomPanInfo: TpZoomPaninfoDual,
  areas: TpChartAreaDefinition[],
  colorSchema: TpChartColorSchema,
  isOverlay: boolean,
) {
  const width = canvasElem.width;
  const height = canvasElem.height;

  const mappedAreas: { [id: string]: { mapX: TpZoomPanelCoordMapper; mapY: TpZoomPanelCoordMapper } } = {};

  for (const area of areas)
    if (!isOverlay || area.renderOverlay) {
      let { mapX, mapY } = generateDualMappers(0, width, 0, height, area, zoomPanInfo, pixelRatio);
      if (area.syncXWith) {
        if (!mappedAreas[area.syncXWith.id]) throw `Synced area not rendered: ${area.syncXWith.id}`;
        mapX = mappedAreas[area.syncXWith.id].mapX;
      }
      if (area.syncYWith) {
        if (!mappedAreas[area.syncYWith.id]) throw `Synced area not rendered: ${area.syncYWith.id}`;
        mapY = mappedAreas[area.syncYWith.id].mapY;
      }
      mappedAreas[area.id] = { mapX, mapY };
      canvasContext.save();
      canvasContext.beginPath();
      canvasContext.moveTo(mapX.renderMin, mapY.renderMin);
      canvasContext.lineTo(mapX.renderMax, mapY.renderMin);
      canvasContext.lineTo(mapX.renderMax, mapY.renderMax);
      canvasContext.lineTo(mapX.renderMin, mapY.renderMax);
      canvasContext.closePath();
      canvasContext.clip();

      const areaRenderContext: TpAreaRenderContext = {
        areaXMin: mapX.renderMin,
        areaXMax: mapX.renderMax,
        areaYMin: mapY.renderMin,
        areaYMax: mapY.renderMax,
        canvasElem,
        canvasContext,
        pixelRatio,
        mapx: mapX.map,
        mapy: mapY.map,
        mapinvx: mapX.mapInv,
        mapinvy: mapY.mapInv,
        logicalRangeX: mapX.logicalRange,
        logicalRangeY: mapY.logicalRange,
        mapsize: (size: number): number => size * pixelRatio,
        mapsizeinv: (size: number): number => size / pixelRatio,
        setFont: (size, bold) => setCanvasFont(canvasContext, pixelRatio, size, bold),
        pixelConv: (size: number) => size * pixelRatio,
        pixelInvConv: (size: number) => size / pixelRatio,
        colorSchema,
      };

      areaRenderContext.setFont(7);
      canvasContext.lineWidth = areaRenderContext.pixelConv(0.75);
      canvasContext.fillStyle = colorSchema.foreColor.toCanvasString();
      canvasContext.textAlign = 'left';
      canvasContext.textBaseline = 'top';

      if (!isOverlay) area.render(areaRenderContext);
      else if (area.renderOverlay) area.renderOverlay(areaRenderContext);
      canvasContext.restore();
    }
}

// Add the rendering of WebGL layers to the 2D canvas in order to be able to export the entire image as a single bitmap
export function renderWebGL2DForDownload(
  canvasElem: any,
  canvasContext: any,
  pixelRatio: number,
  zoomPanInfo: TpZoomPaninfoDual,
  areas: TpChartAreaDefinition[],
  colorSchema: TpChartColorSchema,
  webGLContexts: { [id: string]: TpWebGl2DContext },
) {
  for (const area of areas) {
    if (area.webGL2DRenderData) {
      const posXMin = getRenderPos(0, canvasElem.width, pixelRatio, area.renderRangeX.rmin);
      const posXMax = getRenderPos(0, canvasElem.width, pixelRatio, area.renderRangeX.rmax);
      const posYMin = getRenderPos(0, canvasElem.height, pixelRatio, area.renderRangeY.rmin);
      const posYMax = getRenderPos(0, canvasElem.height, pixelRatio, area.renderRangeY.rmax);
      const webGlContext = webGLContexts[area.id];
      const bm = webGlContext.canvas as any;
      canvasContext.drawImage(bm, posXMin, posYMin, posXMax - posXMin, posYMax - posYMin);
    }
  }
}

function convertZoomPan2WebGlRange(logicalRange: TpLogicalRange, zoomPan: TpZoomPanInfo): TpWebGl2DScale {
  const shiftedMin = logicalRange.rmin - zoomPan.pan * (logicalRange.rmax - logicalRange.rmin);
  const shiftedMax = logicalRange.rmax - zoomPan.pan * (logicalRange.rmax - logicalRange.rmin);
  return {
    sMin: shiftedMin,
    sMax: shiftedMin + (shiftedMax - shiftedMin) / zoomPan.zoomFactor,
  };
}

export function convertZoomPan2WebGlRangeDual(
  area: TpChartAreaDefinition,
  zoomPanInfo: TpZoomPaninfoDual,
  renderWidth: number,
  renderHeight: number,
  pixelRatio: number,
): { scaleX: TpWebGl2DScale; scaleY: TpWebGl2DScale } {
  const logicalRangeX = { ...area.logicalRangeX! };
  const logicalRangeY = { ...area.logicalRangeY! };

  if (area.forceSquareApectRatio) {
    const renderMinX = getRenderPos(0, renderWidth, pixelRatio, area.renderRangeX.rmin);
    const renderMaxX = getRenderPos(0, renderWidth, pixelRatio, area.renderRangeX.rmax);
    const renderMinY = getRenderPos(0, renderHeight, pixelRatio, area.renderRangeY.rmin);
    const renderMaxY = getRenderPos(0, renderHeight, pixelRatio, area.renderRangeY.rmax);
    const imageAspectRatio = (renderMaxY - renderMinY) / (renderMaxX - renderMinX);
    const logicalRangeValueX = area.logicalRangeX!.rmax - area.logicalRangeX!.rmin;
    const logicalRangeValueY = area.logicalRangeY!.rmax - area.logicalRangeY!.rmin;
    const logicalAspectRatio = logicalRangeValueY / logicalRangeValueX;
    if (imageAspectRatio > logicalAspectRatio) {
      // we need to compress the Y axis,  by expanding the logical Y range
      const rangeExpansion = (logicalRangeValueY / logicalAspectRatio) * imageAspectRatio - logicalRangeValueY;
      logicalRangeY!.rmin -= rangeExpansion / 2;
      logicalRangeY!.rmax += rangeExpansion / 2;
    } else {
      // we need to compress the X axis, by expanding the logical X range
      const rangeExpansion = (logicalRangeValueX / imageAspectRatio) * logicalAspectRatio - logicalRangeValueX;
      logicalRangeX!.rmin -= rangeExpansion / 2;
      logicalRangeX!.rmax += rangeExpansion / 2;
    }
  }

  return {
    scaleX: convertZoomPan2WebGlRange(logicalRangeX, zoomPanInfo.x),
    scaleY: convertZoomPan2WebGlRange(logicalRangeY, zoomPanInfo.y),
  };
}

export function renderZoomPanWebGL2D(
  zoomPanInfo: TpZoomPaninfoDual,
  areas: TpChartAreaDefinition[],
  webGLContexts: { [id: string]: TpWebGl2DContext },
  renderWidth: number,
  renderHeight: number,
  pixelRatio: number,
) {
  for (const area of areas) {
    if (area.webGL2DRenderData && webGLContexts[area.id]) {
      const ctx = webGLContexts[area.id];
      const scalers = convertZoomPan2WebGlRangeDual(area, zoomPanInfo, renderWidth, renderHeight, pixelRatio);
      ctx.scaleX = scalers.scaleX;
      ctx.scaleY = scalers.scaleY;

      ctx.zoomFactor = (zoomPanInfo.x.zoomFactor + zoomPanInfo.y.zoomFactor) / 2;
      renderWebGL2D(ctx);
    }
  }
}

export function renderHoverLabels(
  canvas: any,
  ctx: any,
  pixelRatio: number,
  colorSchema: TpChartColorSchema,
  hoverCenterX: number | null,
  hoverCenterY: number | null,
  labels: string[],
) {
  const hasLabels = labels.length > 0;
  let arrowLineTop = 0;
  if (hoverCenterY !== null) arrowLineTop = hasLabels ? 0 : hoverCenterY - 60 * pixelRatio;

  const hoverColor = color(0, 100, 140);

  if (hoverCenterX !== null && hoverCenterY !== null) {
    ctx.filter = 'blur(4px)';
    ctx.fillStyle = colorSchema.backColor.toCanvasString();
    ctx.fillRect(
      hoverCenterX - 3 * pixelRatio,
      arrowLineTop,
      6 * pixelRatio,
      hoverCenterY - 10 * pixelRatio - arrowLineTop,
    );
    ctx.beginPath();
    ctx.moveTo(hoverCenterX, hoverCenterY - 7 * pixelRatio);
    ctx.lineTo(hoverCenterX - 7 * pixelRatio, hoverCenterY - 22 * pixelRatio);
    ctx.lineTo(hoverCenterX + 7 * pixelRatio, hoverCenterY - 22 * pixelRatio);
    ctx.closePath();
    ctx.fill();

    ctx.filter = 'blur(0px)';
    ctx.fillStyle = hoverColor.toCanvasString();
    ctx.fillRect(
      hoverCenterX - pixelRatio,
      arrowLineTop,
      2 * pixelRatio,
      hoverCenterY - 10 * pixelRatio - arrowLineTop,
    );

    ctx.fillStyle = hoverColor.toCanvasString();
    ctx.beginPath();
    ctx.moveTo(hoverCenterX, hoverCenterY - 8 * pixelRatio);
    ctx.lineTo(hoverCenterX - 5 * pixelRatio, hoverCenterY - 20 * pixelRatio);
    ctx.lineTo(hoverCenterX + 5 * pixelRatio, hoverCenterY - 20 * pixelRatio);
    ctx.closePath();
    ctx.fill();
  }

  if (!hasLabels) return;

  const grd = ctx.createLinearGradient(0, 0, canvas.width, 0);
  grd.addColorStop(0, hoverColor.toCanvasString());
  grd.addColorStop(0.7, hoverColor.lighten(0.4).toCanvasString());
  grd.addColorStop(1, hoverColor.lighten(0.25).toCanvasString());
  ctx.fillStyle = grd;

  // ctx.fillStyle = hoverColor;
  const labelCount = labels.length;
  // Use minimum to get a uniform looking label box.
  const minLineCount = 3;
  // Could be changed to allow more lines, but requires adjustments to the layout (e.g. menu location)
  const maxLineCount = 3;
  const lineCount = Math.max(minLineCount, Math.min(labelCount, maxLineCount));

  const baseYOffset = 5 * pixelRatio;
  const lineHeight = 15 * pixelRatio;
  const restYMargin = 2 * pixelRatio;
  const totalHeight = lineCount * lineHeight + baseYOffset + restYMargin;

  ctx.fillRect(0, 0, canvas.width, totalHeight);

  ctx.font = `${10 * pixelRatio}pt Arial`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  // let maxLabelWidth = 1;
  let xOffset = 7 * pixelRatio;
  let yOffset = baseYOffset;
  let maxW = 0;
  ctx.fillStyle = 'white';
  for (const label of labels) {
    maxW = Math.max(maxW, ctx.measureText(label).width);
    ctx.fillText(label, xOffset, yOffset);
    yOffset += lineHeight;
    if (yOffset + lineHeight + restYMargin > totalHeight) {
      xOffset += maxW + 5 * pixelRatio;
      yOffset = baseYOffset;
      maxW = 0;
    }
  }
}

export function calcZoom(
  currentZoomPan: TpZoomPanInfo,
  mapper: any,
  zoomFactorMul: number,
  centerLogicalCoord: number,
  maxZoomFactor: number | undefined | null,
): TpZoomPanInfo {
  let zoomFactor = Math.max(1, currentZoomPan.zoomFactor * zoomFactorMul);
  if (maxZoomFactor) zoomFactor = Math.min(zoomFactor, maxZoomFactor);
  const pan = currentZoomPan.pan;
  const lf = (centerLogicalCoord - mapper.logicalRange.rmin) / (mapper.logicalRange.rmax - mapper.logicalRange.rmin);
  return {
    zoomFactor,
    pan: (currentZoomPan.zoomFactor / zoomFactor) * (lf + pan) - lf,
  };
}

export function calcPan(currentZoomPan: TpZoomPanInfo, mapper: any, panDist: number): TpZoomPanInfo {
  return {
    zoomFactor: currentZoomPan.zoomFactor,
    pan: currentZoomPan.pan - panDist * mapper.panConversionFactor,
  };
}

export function clipZoomPan(zoomPan: TpZoomPanInfo): TpZoomPanInfo {
  const rs = {
    zoomFactor: Math.max(1, zoomPan.zoomFactor),
    pan: zoomPan.pan,
  };
  rs.pan = Math.min(rs.pan, 0);
  rs.pan = Math.max(rs.pan, -(1 - 1 / rs.zoomFactor));
  return rs;
}
